import rawConfig from '../data/config.json';
import { getPatientDefinition, getRoomDefinition } from '../sim/Simulation';
import type { Patient, PlacedRoom, SimState, Staff } from '../sim/types';
import { CURRENT_SCHEMA_VERSION, type LoadFailReason, type SaveEnvelope } from './types';

export interface EnvelopeValidationResult {
  ok: boolean;
  reason?: LoadFailReason;
  envelope?: SaveEnvelope;
}

export function validateEnvelope(raw: unknown): EnvelopeValidationResult {
  if (!raw || typeof raw !== 'object') {
    return { ok: false, reason: 'INVALID_ENVELOPE' };
  }

  const obj = raw as Record<string, unknown>;

  if (typeof obj.schemaVersion !== 'number' || !Number.isInteger(obj.schemaVersion) || obj.schemaVersion < 1) {
    return { ok: false, reason: 'INVALID_ENVELOPE' };
  }

  if (obj.schemaVersion > CURRENT_SCHEMA_VERSION) {
    return { ok: false, reason: 'NEWER_VERSION' };
  }

  if (typeof obj.savedAt !== 'string' || obj.savedAt.trim() === '') {
    return { ok: false, reason: 'INVALID_ENVELOPE' };
  }

  if (!obj.state || typeof obj.state !== 'object') {
    return { ok: false, reason: 'INVALID_ENVELOPE' };
  }

  return { ok: true, envelope: raw as SaveEnvelope };
}

export interface StateValidationResult {
  ok: boolean;
  reason?: LoadFailReason;
  message?: string;
}

export function validateState(state: SimState): StateValidationResult {
  // 1. Tipe dasar & Invariant Uang & Waktu
  if (!Number.isInteger(state.money)) {
    return { ok: false, reason: 'INVALID_STATE', message: 'Uang harus integer' };
  }

  if (!Number.isInteger(state.totalGameMs) || state.totalGameMs < 0) {
    return { ok: false, reason: 'INVALID_STATE', message: 'totalGameMs harus integer >= 0' };
  }

  if (!Number.isInteger(state.reputation) || state.reputation < 0 || state.reputation > 100) {
    return { ok: false, reason: 'INVALID_STATE', message: 'reputation harus integer rentang 0-100' };
  }

  if (typeof state.isPaused !== 'boolean') {
    return { ok: false, reason: 'INVALID_STATE', message: 'isPaused harus boolean' };
  }

  if (![1, 2, 5].includes(state.speedMultiplier)) {
    return { ok: false, reason: 'INVALID_STATE', message: 'speedMultiplier tidak valid' };
  }

  // 2. Validasi Grid Config
  const grid = state.gridConfig;
  if (
    !grid ||
    grid.cols !== rawConfig.grid.cols ||
    grid.floors !== rawConfig.grid.floors ||
    grid.stairCol !== rawConfig.grid.stairCol ||
    grid.entranceCol !== rawConfig.grid.entranceCol
  ) {
    return { ok: false, reason: 'INVALID_STATE', message: 'gridConfig tidak sesuai' };
  }

  // 3. Validasi ID Generator
  if (!Number.isInteger(state.nextRoomId) || !Number.isInteger(state.nextPatientId) || !Number.isInteger(state.nextStaffId)) {
    return { ok: false, reason: 'INVALID_STATE', message: 'ID generator harus integer' };
  }

  // 4. Validasi Ruangan & Tata Letak Bangunan
  if (!Array.isArray(state.rooms)) {
    return { ok: false, reason: 'INVALID_STATE', message: 'rooms harus berupa array' };
  }

  const roomIds = new Set<number>();
  let maxRoomId = 0;

  // Grid matriks untuk mendeteksi overlap dan penyangga struktural
  const occupiedGrid: (number | null)[][] = Array.from({ length: grid.floors }, () =>
    Array.from({ length: grid.cols }, () => null)
  );

  for (const r of state.rooms) {
    // Validasi bentuk PlacedRoom
    if (!isValidPlacedRoom(r)) {
      return { ok: false, reason: 'INVALID_STATE', message: 'Bentuk PlacedRoom tidak valid' };
    }

    // Cek konten typeId
    const roomDef = getRoomDefinition(r.typeId);
    if (!roomDef) {
      return { ok: false, reason: 'UNKNOWN_CONTENT_ID', message: `Unknown room typeId: ${r.typeId}` };
    }

    if (r.width !== roomDef.width) {
      return { ok: false, reason: 'INVALID_STATE', message: `Lebar ruangan tidak sesuai untuk ${r.typeId}` };
    }

    // Keunikan ID & nextRoomId > max ID
    if (roomIds.has(r.id)) {
      return { ok: false, reason: 'INVALID_STATE', message: `ID ruangan ganda: ${r.id}` };
    }
    roomIds.add(r.id);
    if (r.id > maxRoomId) maxRoomId = r.id;

    // Aturan Batas Grid
    if (r.floor < 0 || r.floor >= grid.floors || r.startCol < 0 || r.startCol + r.width > grid.cols) {
      return { ok: false, reason: 'INVALID_STATE', message: `Ruangan keluar dari batas grid` };
    }

    // Aturan Petak Terlarang: Pintu masuk (0, 0) dan Kolom Tangga (6)
    for (let c = r.startCol; c < r.startCol + r.width; c++) {
      if (r.floor === 0 && c === grid.entranceCol) {
        return { ok: false, reason: 'INVALID_STATE', message: `Ruangan menimpa pintu masuk` };
      }
      if (c === grid.stairCol) {
        return { ok: false, reason: 'INVALID_STATE', message: `Ruangan menimpa kolom tangga` };
      }

      // Aturan Overlap
      if (occupiedGrid[r.floor][c] !== null) {
        return { ok: false, reason: 'INVALID_STATE', message: `Ruangan tumpang tindih (overlap)` };
      }
      occupiedGrid[r.floor][c] = r.id;
    }
  }

  if (state.rooms.length > 0 && state.nextRoomId <= maxRoomId) {
    return { ok: false, reason: 'INVALID_STATE', message: 'nextRoomId harus lebih besar dari semua ID ruangan' };
  }

  // Aturan Penyangga Struktural Lantai Atas (Structural Support Rule)
  for (const r of state.rooms) {
    if (r.floor > 0) {
      for (let c = r.startCol; c < r.startCol + r.width; c++) {
        const floorBelow = r.floor - 1;
        const supportedByEntrance = floorBelow === 0 && c === grid.entranceCol;
        const supportedByRoom = occupiedGrid[floorBelow][c] !== null;

        if (!supportedByEntrance && !supportedByRoom) {
          return { ok: false, reason: 'INVALID_STATE', message: `Ruangan di lantai atas tidak memiliki penyangga penuh` };
        }
      }
    }
  }

  // 5. Validasi Pasien
  if (!Array.isArray(state.patients)) {
    return { ok: false, reason: 'INVALID_STATE', message: 'patients harus array' };
  }
  for (const p of state.patients) {
    if (!isValidPatient(p)) {
      return { ok: false, reason: 'INVALID_STATE', message: 'Bentuk Patient tidak valid' };
    }
    const patientDef = getPatientDefinition(p.typeId);
    if (!patientDef) {
      return { ok: false, reason: 'UNKNOWN_CONTENT_ID', message: `Unknown patient typeId: ${p.typeId}` };
    }
    if (p.assignedRoomId !== null && !roomIds.has(p.assignedRoomId)) {
      return { ok: false, reason: 'INVALID_STATE', message: `assignedRoomId tidak ada di daftar ruangan` };
    }
  }

  // 6. Validasi Staf
  if (!Array.isArray(state.staff)) {
    return { ok: false, reason: 'INVALID_STATE', message: 'staff harus array' };
  }
  for (const s of state.staff) {
    if (!isValidStaff(s)) {
      return { ok: false, reason: 'INVALID_STATE', message: 'Bentuk Staff tidak valid' };
    }
    if (!roomIds.has(s.assignedRoomId)) {
      return { ok: false, reason: 'INVALID_STATE', message: `Staf ditugaskan ke ruangan yang tidak ada` };
    }
  }

  return { ok: true };
}

function isValidPlacedRoom(r: unknown): r is PlacedRoom {
  if (!r || typeof r !== 'object') return false;
  const o = r as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    Number.isInteger(o.id) &&
    typeof o.typeId === 'string' &&
    typeof o.floor === 'number' &&
    Number.isInteger(o.floor) &&
    typeof o.startCol === 'number' &&
    Number.isInteger(o.startCol) &&
    typeof o.width === 'number' &&
    Number.isInteger(o.width)
  );
}

function isValidPatient(p: unknown): p is Patient {
  if (!p || typeof p !== 'object') return false;
  const o = p as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    Number.isInteger(o.id) &&
    typeof o.typeId === 'string' &&
    typeof o.state === 'string' &&
    typeof o.floor === 'number' &&
    Number.isInteger(o.floor) &&
    typeof o.col === 'number' &&
    Number.isInteger(o.col) &&
    typeof o.targetFloor === 'number' &&
    Number.isInteger(o.targetFloor) &&
    typeof o.targetCol === 'number' &&
    Number.isInteger(o.targetCol) &&
    typeof o.patienceRemainingMs === 'number' &&
    typeof o.totalPatienceMs === 'number' &&
    (o.assignedRoomId === null || (typeof o.assignedRoomId === 'number' && Number.isInteger(o.assignedRoomId)))
  );
}

function isValidStaff(s: unknown): s is Staff {
  if (!s || typeof s !== 'object') return false;
  const o = s as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    Number.isInteger(o.id) &&
    (o.role === 'receptionist' || o.role === 'doctor') &&
    typeof o.assignedRoomId === 'number' &&
    Number.isInteger(o.assignedRoomId) &&
    typeof o.floor === 'number' &&
    Number.isInteger(o.floor) &&
    typeof o.col === 'number' &&
    Number.isInteger(o.col) &&
    typeof o.isBusy === 'boolean' &&
    (o.currentPatientId === null || typeof o.currentPatientId === 'number') &&
    typeof o.busyRemainingMs === 'number'
  );
}

